import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaWhatsapp, FaInstagram, FaFacebook, FaGoogle, FaKey, FaLink, FaEye, FaEyeSlash, FaQuestionCircle, FaBook, FaExternalLinkAlt, FaRobot } from 'react-icons/fa';
import { API_URL } from '../config/api';
import HelpModal from './common/HelpModal';
import './AIConfigManager.css';

const AIConfigManager = () => {
  const [activeTab, setActiveTab] = useState('whatsapp');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [oauthPopup, setOauthPopup] = useState(null);
  const [helpModal, setHelpModal] = useState({ show: false, title: '', content: '' });
  const [formData, setFormData] = useState({
    whatsapp: {
      apiUrl: '',
      phoneNumberId: '',
      accessToken: '',
      verifyToken: '',
      webhookUrl: ''
    },
    meta: {
      accessToken: '',
      apiVersion: 'v18.0',
      facebookPageId: '',
      instagramAccountId: '',
      appId: '',
      appSecret: ''
    },
    google: {
      clientId: '',
      clientSecret: '',
      refreshToken: '',
      customerId: '',
      developerToken: '',
      redirectUri: ''
    },
    openrouter: {
      apiKey: '',
      baseUrl: 'https://openrouter.ai/api/v1',
      defaultModel: 'openai/gpt-3.5-turbo',
      models: [], // Additional models (in addition to default)
      organizationId: ''
    }
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchConfigurations();
  }, []);

  // Listen for OAuth callback messages
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'OAUTH_CALLBACK') {
        handleOAuthCallback(event.data);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const fetchConfigurations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/ai-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.data.success && response.data.data) {
        setFormData(prev => ({
          ...prev,
          whatsapp: { ...prev.whatsapp, ...response.data.data.whatsapp },
          meta: { ...prev.meta, ...response.data.data.meta },
          google: { ...prev.google, ...response.data.data.google },
          openrouter: { ...prev.openrouter, ...(response.data.data.openrouter || {}) }
        }));
      }
    } catch (error) {
      console.error('Error fetching configurations:', error);
      // Don't show error if no config exists yet
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (platform, field, value) => {
    setFormData(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: value
      }
    }));
  };

  const handleSave = async (platform) => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/ai-config/${platform}`,
        formData[platform],
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${platform.toUpperCase()} configuration saved successfully!`);
    } catch (error) {
      console.error('Error saving configuration:', error);
      toast.error(error.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleGetOAuthUrl = async (platform) => {
    try {
      const response = await axios.get(`${API_URL}/api/ai-config/${platform}/oauth-url`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.data?.authUrl) {
        openOAuthPopup(response.data.data.authUrl, platform);
      } else {
        toast.error('Failed to get OAuth URL');
      }
    } catch (error) {
      console.error('Error getting OAuth URL:', error);
      toast.error(error.response?.data?.message || 'Failed to get OAuth URL');
    }
  };

  const openOAuthPopup = (url, platform) => {
    const width = 600;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const popup = window.open(
      url,
      `${platform}_oauth`,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    setOauthPopup({ window: popup, platform });

    // Check if popup is closed
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        setOauthPopup(null);
      }
    }, 1000);
  };

  const handleOAuthCallback = async (data) => {
    const { platform, code, error } = data;

    if (error) {
      toast.error(`OAuth error: ${error}`);
      return;
    }

    if (!code) {
      toast.error('No authorization code received');
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/ai-config/${platform}/oauth-callback`,
        { code },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`${platform.toUpperCase()} OAuth authorization successful!`);
        
        // Update form data with new tokens
        if (response.data.data) {
          setFormData(prev => ({
            ...prev,
            [platform]: { ...prev[platform], ...response.data.data }
          }));
        }

        // Close popup
        if (oauthPopup?.window) {
          oauthPopup.window.close();
        }
        setOauthPopup(null);
      }
    } catch (error) {
      console.error('Error handling OAuth callback:', error);
      toast.error(error.response?.data?.message || 'Failed to complete OAuth authorization');
    }
  };

  const togglePasswordVisibility = (platform, field) => {
    setShowPasswords(prev => ({
      ...prev,
      [`${platform}_${field}`]: !prev[`${platform}_${field}`]
    }));
  };

  const showHelp = (platform) => {
    const helpContent = getHelpContent(platform);
    setHelpModal({
      show: true,
      title: helpContent.title,
      content: helpContent.content
    });
  };

  const getHelpContent = (platform) => {
    switch (platform) {
      case 'openrouter':
        return {
          title: 'OpenRouter Setup Guide',
          content: `
            <div style="text-align: left; line-height: 1.8;">
              <h3 style="color: #10B981; margin-bottom: 15px;">🤖 OpenRouter Setup Guide</h3>
              <p style="margin-bottom: 20px;">OpenRouter provides access to multiple LLM models through a single API.</p>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 1: Create OpenRouter Account</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>Go to <a href="https://openrouter.ai/" target="_blank" style="color: #10B981;">OpenRouter</a></li>
                <li>Sign up for a free account</li>
                <li>Verify your email address</li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 2: Get API Key</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>Go to <a href="https://openrouter.ai/keys" target="_blank" style="color: #10B981;">API Keys</a></li>
                <li>Click <strong>"Create Key"</strong></li>
                <li>Copy your API key</li>
                <li>Paste it in the form above</li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 3: Choose Default Model</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>Browse available models at <a href="https://openrouter.ai/models" target="_blank" style="color: #10B981;">OpenRouter Models</a></li>
                <li>Popular options:
                  <ul style="margin-left: 20px; margin-top: 10px;">
                    <li><strong>openai/gpt-3.5-turbo</strong> - Fast and affordable</li>
                    <li><strong>openai/gpt-4</strong> - More capable, higher cost</li>
                    <li><strong>anthropic/claude-3-sonnet</strong> - Great for long conversations</li>
                    <li><strong>google/gemini-pro</strong> - Google's latest model</li>
                  </ul>
                </li>
                <li>Enter the model identifier in the "Default Model" field</li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 4: Optional - Organization ID</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>If you have an organization account, enter your Organization ID</li>
                <li>This helps with usage tracking and billing</li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">📚 Additional Resources</h4>
              <ul style="margin-left: 20px;">
                <li><a href="https://openrouter.ai/docs" target="_blank" style="color: #10B981;">OpenRouter Documentation</a></li>
                <li><a href="https://openrouter.ai/models" target="_blank" style="color: #10B981;">Available Models</a></li>
                <li><a href="https://openrouter.ai/pricing" target="_blank" style="color: #10B981;">Pricing Information</a></li>
              </ul>
            </div>
          `
        };
      case 'meta':
        return {
          title: 'Meta (Facebook/Instagram) Setup Guide',
          content: `
            <div style="text-align: left; line-height: 1.8;">
              <h3 style="color: #1877F2; margin-bottom: 15px;">📘 Step-by-Step Setup Guide</h3>
              
              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 1: Create a Facebook App</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>Go to <a href="https://developers.facebook.com/apps/" target="_blank" style="color: #1877F2;">Facebook Developers</a></li>
                <li>Click <strong>"Create App"</strong> button</li>
                <li>Select <strong>"Business"</strong> as the app type</li>
                <li>Fill in your app details (name, contact email)</li>
                <li>Click <strong>"Create App"</strong></li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 2: Get Your App Credentials</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>In your app dashboard, go to <strong>"Settings" → "Basic"</strong></li>
                <li>Copy your <strong>App ID</strong> and <strong>App Secret</strong></li>
                <li>Paste them in the form above</li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 3: Add Products & Permissions</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>Go to <strong>"Products"</strong> in the left sidebar</li>
                <li>Add <strong>"Facebook Login"</strong> product</li>
                <li>Add <strong>"Instagram Basic Display"</strong> product (for Instagram)</li>
                <li>Add <strong>"Instagram Graph API"</strong> product (for Instagram posting)</li>
                <li>Add <strong>"Pages"</strong> product (for Facebook posting)</li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 4: Configure OAuth Redirect URI</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>Go to <strong>"Settings" → "Basic"</strong></li>
                <li>In <strong>"Valid OAuth Redirect URIs"</strong>, add:
                  <br/><code style="background: #f5f5f5; padding: 5px 10px; border-radius: 4px; display: inline-block; margin-top: 5px;">
                    ${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/ai-config/meta/oauth-callback
                  </code>
                </li>
                <li>Click <strong>"Save Changes"</strong></li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 5: Get Facebook Page ID</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>Go to your <a href="https://www.facebook.com/pages" target="_blank" style="color: #1877F2;">Facebook Page</a></li>
                <li>Click <strong>"About"</strong> tab</li>
                <li>Scroll down to find your <strong>Page ID</strong></li>
                <li>Or use <a href="https://www.facebook.com/help/1503421039731588" target="_blank" style="color: #1877F2;">this guide</a> to find it</li>
                <li>Paste the Page ID in the form above</li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 6: Get Instagram Business Account ID</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>Connect your Instagram account to your Facebook Page</li>
                <li>Go to <strong>"Instagram"</strong> product in your app</li>
                <li>Click <strong>"Basic Display"</strong> or <strong>"Graph API"</strong></li>
                <li>Find your <strong>Instagram Business Account ID</strong></li>
                <li>Or use the <a href="https://developers.facebook.com/docs/instagram-api/getting-started" target="_blank" style="color: #1877F2;">Graph API Explorer</a></li>
                <li>Paste the Account ID in the form above</li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 7: Authorize & Get Access Token</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>Fill in <strong>App ID</strong> and <strong>App Secret</strong> in the form above</li>
                <li>Click <strong>"Save Configuration"</strong></li>
                <li>Click <strong>"Get OAuth Authorization URL"</strong> button</li>
                <li>A popup will open - authorize the app</li>
                <li>The access token will be automatically saved</li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">📚 Required Permissions</h4>
              <ul style="margin-left: 20px; margin-bottom: 20px; background: #f9f9f9; padding: 15px; border-radius: 5px;">
                <li><code>pages_show_list</code> - List your Facebook Pages</li>
                <li><code>pages_read_engagement</code> - Read page engagement</li>
                <li><code>instagram_basic</code> - Basic Instagram access</li>
                <li><code>instagram_content_publish</code> - Publish to Instagram</li>
                <li><code>business_management</code> - Manage business assets</li>
              </ul>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">🔗 Useful Links</h4>
              <ul style="margin-left: 20px; margin-bottom: 20px;">
                <li><a href="https://developers.facebook.com/docs/graph-api/" target="_blank" style="color: #1877F2;">Graph API Documentation</a></li>
                <li><a href="https://developers.facebook.com/docs/instagram-api/" target="_blank" style="color: #1877F2;">Instagram API Documentation</a></li>
                <li><a href="https://developers.facebook.com/tools/explorer/" target="_blank" style="color: #1877F2;">Graph API Explorer</a></li>
                <li><a href="https://developers.facebook.com/docs/facebook-login/web" target="_blank" style="color: #1877F2;">Facebook Login Guide</a></li>
              </ul>

              <div style="background: #e3f2fd; padding: 15px; border-radius: 5px; margin-top: 20px;">
                <strong>💡 Tip:</strong> Make sure your app is in <strong>"Development"</strong> mode for testing. 
                For production, submit your app for review to get all permissions approved.
              </div>
            </div>
          `
        };
      case 'whatsapp':
        return {
          title: 'WhatsApp Business API Setup Guide',
          content: `
            <div style="text-align: left; line-height: 1.8;">
              <h3 style="color: #25D366; margin-bottom: 15px;">📱 Step-by-Step Setup Guide</h3>
              
              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 1: Create a Meta App</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>Go to <a href="https://developers.facebook.com/apps/" target="_blank" style="color: #1877F2;">Facebook Developers</a></li>
                <li>Click <strong>"Create App"</strong></li>
                <li>Select <strong>"Business"</strong> type</li>
                <li>Add <strong>"WhatsApp"</strong> product to your app</li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 2: Get WhatsApp Credentials</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>Go to <strong>"WhatsApp" → "API Setup"</strong> in your app</li>
                <li>Copy your <strong>Phone Number ID</strong></li>
                <li>Generate a <strong>System User Access Token</strong></li>
                <li>Copy the <strong>Access Token</strong></li>
                <li>Paste them in the form above</li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 3: Configure Webhook</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>Go to <strong>"WhatsApp" → "Configuration"</strong></li>
                <li>Set <strong>Webhook URL</strong> to:
                  <br/><code style="background: #f5f5f5; padding: 5px 10px; border-radius: 4px; display: inline-block; margin-top: 5px;">
                    ${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/whatsapp/webhook
                  </code>
                </li>
                <li>Set a <strong>Verify Token</strong> (any random string)</li>
                <li>Save the verify token in the form above</li>
                <li>Click <strong>"Verify and Save"</strong></li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">🔗 Useful Links</h4>
              <ul style="margin-left: 20px; margin-bottom: 20px;">
                <li><a href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started" target="_blank" style="color: #25D366;">WhatsApp Cloud API Guide</a></li>
                <li><a href="https://developers.facebook.com/docs/whatsapp/cloud-api/webhooks" target="_blank" style="color: #25D366;">Webhook Setup Guide</a></li>
              </ul>
            </div>
          `
        };
      case 'google':
        return {
          title: 'Google Ads API Setup Guide',
          content: `
            <div style="text-align: left; line-height: 1.8;">
              <h3 style="color: #4285F4; margin-bottom: 15px;">📊 Step-by-Step Setup Guide</h3>
              
              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 1: Create Google Cloud Project</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>Go to <a href="https://console.cloud.google.com/" target="_blank" style="color: #4285F4;">Google Cloud Console</a></li>
                <li>Create a new project or select existing one</li>
                <li>Enable <strong>"Google Ads API"</strong></li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 2: Create OAuth Credentials</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>Go to <strong>"APIs & Services" → "Credentials"</strong></li>
                <li>Click <strong>"Create Credentials" → "OAuth client ID"</strong></li>
                <li>Select <strong>"Web application"</strong></li>
                <li>Add authorized redirect URI:
                  <br/><code style="background: #f5f5f5; padding: 5px 10px; border-radius: 4px; display: inline-block; margin-top: 5px;">
                    ${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/ai-config/google/oauth-callback
                  </code>
                </li>
                <li>Copy <strong>Client ID</strong> and <strong>Client Secret</strong></li>
                <li>Paste them in the form above</li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 3: Get Developer Token</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>Go to <a href="https://ads.google.com/" target="_blank" style="color: #4285F4;">Google Ads</a></li>
                <li>Go to <strong>"Tools & Settings" → "API Center"</strong></li>
                <li>Apply for a <strong>Developer Token</strong></li>
                <li>Once approved, copy the token</li>
                <li>Paste it in the form above</li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 4: Get Customer ID</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>In Google Ads, go to your account</li>
                <li>Your <strong>Customer ID</strong> is in the top right (format: XXX-XXX-XXXX)</li>
                <li>Paste it in the form above (without dashes)</li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">Step 5: Authorize & Get Refresh Token</h4>
              <ol style="margin-left: 20px; margin-bottom: 20px;">
                <li>Fill in <strong>Client ID</strong> and <strong>Client Secret</strong></li>
                <li>Click <strong>"Save Configuration"</strong></li>
                <li>Click <strong>"Get OAuth Authorization URL"</strong></li>
                <li>Authorize the app in the popup</li>
                <li>The refresh token will be automatically saved</li>
              </ol>

              <h4 style="color: #333; margin-top: 20px; margin-bottom: 10px;">🔗 Useful Links</h4>
              <ul style="margin-left: 20px; margin-bottom: 20px;">
                <li><a href="https://developers.google.com/google-ads/api/docs/get-started" target="_blank" style="color: #4285F4;">Google Ads API Guide</a></li>
                <li><a href="https://developers.google.com/google-ads/api/docs/oauth/overview" target="_blank" style="color: #4285F4;">OAuth Setup Guide</a></li>
              </ul>
            </div>
          `
        };
      default:
        return { title: 'Help', content: 'No help available for this platform.' };
    }
  };

  const platforms = [
    { id: 'whatsapp', name: 'WhatsApp', icon: FaWhatsapp, color: '#25D366' },
    { id: 'meta', name: 'Meta (Facebook/Instagram)', icon: FaFacebook, color: '#1877F2' },
    { id: 'google', name: 'Google Ads', icon: FaGoogle, color: '#4285F4' },
    { id: 'openrouter', name: 'OpenRouter (LLM)', icon: FaRobot, color: '#10B981' },
  ];

  const ActiveIcon = platforms.find(p => p.id === activeTab)?.icon || FaKey;

  if (loading) {
    return (
      <div className="ai-config-manager">
        <div className="loading">Loading configurations...</div>
      </div>
    );
  }

  return (
    <div className="ai-config-manager">
      <div className="header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div>
            <h2>🔐 AI Configuration Manager</h2>
            <p className="subtitle">Configure API credentials for automation services</p>
          </div>
          <button
            onClick={() => showHelp(activeTab)}
            className="btn btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            title="View Setup Guide"
          >
            <FaQuestionCircle />
            <span>Setup Guide</span>
          </button>
        </div>
      </div>

      {/* Platform Tabs */}
      <div className="platform-tabs">
        {platforms.map((platform) => {
          const Icon = platform.icon;
          return (
            <button
              key={platform.id}
              className={`platform-tab ${activeTab === platform.id ? 'active' : ''}`}
              onClick={() => setActiveTab(platform.id)}
              style={{ '--platform-color': platform.color }}
            >
              <Icon />
              <span>{platform.name}</span>
            </button>
          );
        })}
      </div>

      {/* Configuration Forms */}
      <div className="config-content">
        {/* WhatsApp Configuration */}
        {activeTab === 'whatsapp' && (
          <div className="config-form">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>
                <FaWhatsapp style={{ marginRight: '10px', color: '#25D366' }} />
                WhatsApp Business API Configuration
              </h3>
              <button
                onClick={() => showHelp('whatsapp')}
                className="btn btn-link"
                style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#25D366' }}
                title="WhatsApp Setup Guide"
              >
                <FaBook />
                <span>How to Setup</span>
              </button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>API URL *</label>
                <input
                  type="text"
                  value={formData.whatsapp.apiUrl}
                  onChange={(e) => handleInputChange('whatsapp', 'apiUrl', e.target.value)}
                  placeholder="https://graph.facebook.com/v18.0"
                />
              </div>
              <div className="form-group">
                <label>Phone Number ID *</label>
                <input
                  type="text"
                  value={formData.whatsapp.phoneNumberId}
                  onChange={(e) => handleInputChange('whatsapp', 'phoneNumberId', e.target.value)}
                  placeholder="Your WhatsApp Phone Number ID"
                />
              </div>
              <div className="form-group">
                <label>Access Token *</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords['whatsapp_accessToken'] ? 'text' : 'password'}
                    value={formData.whatsapp.accessToken}
                    onChange={(e) => handleInputChange('whatsapp', 'accessToken', e.target.value)}
                    placeholder="Your WhatsApp Access Token"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => togglePasswordVisibility('whatsapp', 'accessToken')}
                  >
                    {showPasswords['whatsapp_accessToken'] ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Verify Token</label>
                <input
                  type="text"
                  value={formData.whatsapp.verifyToken}
                  onChange={(e) => handleInputChange('whatsapp', 'verifyToken', e.target.value)}
                  placeholder="Webhook verification token"
                />
              </div>
              <div className="form-group full-width">
                <label>Webhook URL</label>
                <input
                  type="text"
                  value={formData.whatsapp.webhookUrl}
                  onChange={(e) => handleInputChange('whatsapp', 'webhookUrl', e.target.value)}
                  placeholder="https://yourdomain.com/api/whatsapp/webhook"
                  readOnly
                />
                <small>This URL should be configured in your Meta App settings</small>
              </div>
            </div>
            <div className="form-actions">
              <button
                onClick={() => handleSave('whatsapp')}
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        )}

        {/* Meta Configuration */}
        {activeTab === 'meta' && (
          <div className="config-form">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>
                <FaFacebook style={{ marginRight: '10px', color: '#1877F2' }} />
                Meta (Facebook/Instagram) API Configuration
              </h3>
              <button
                onClick={() => showHelp('meta')}
                className="btn btn-link"
                style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#1877F2' }}
                title="Meta Setup Guide"
              >
                <FaBook />
                <span>How to Setup</span>
              </button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>App ID *</label>
                <input
                  type="text"
                  value={formData.meta.appId}
                  onChange={(e) => handleInputChange('meta', 'appId', e.target.value)}
                  placeholder="Your Meta App ID"
                />
              </div>
              <div className="form-group">
                <label>App Secret *</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords['meta_appSecret'] ? 'text' : 'password'}
                    value={formData.meta.appSecret}
                    onChange={(e) => handleInputChange('meta', 'appSecret', e.target.value)}
                    placeholder="Your Meta App Secret"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => togglePasswordVisibility('meta', 'appSecret')}
                  >
                    {showPasswords['meta_appSecret'] ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Access Token</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords['meta_accessToken'] ? 'text' : 'password'}
                    value={formData.meta.accessToken}
                    onChange={(e) => handleInputChange('meta', 'accessToken', e.target.value)}
                    placeholder="Will be obtained via OAuth"
                    readOnly
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => togglePasswordVisibility('meta', 'accessToken')}
                  >
                    {showPasswords['meta_accessToken'] ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>API Version</label>
                <input
                  type="text"
                  value={formData.meta.apiVersion}
                  onChange={(e) => handleInputChange('meta', 'apiVersion', e.target.value)}
                  placeholder="v24.0"
                />
                <small>Latest: v24.0 (as per <a href="https://developers.facebook.com/docs/graph-api/" target="_blank" rel="noopener noreferrer">Graph API docs</a>)</small>
                <div style={{ marginTop: '8px', fontSize: '0.85rem' }}>
                  <a 
                    href="https://developers.facebook.com/apps/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#1877F2', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                  >
                    <FaExternalLinkAlt />
                    Get App ID & Secret from Facebook Developers
                  </a>
                </div>
              </div>
              <div className="form-group">
                <label>Facebook Page ID</label>
                <input
                  type="text"
                  value={formData.meta.facebookPageId}
                  onChange={(e) => handleInputChange('meta', 'facebookPageId', e.target.value)}
                  placeholder="Your Facebook Page ID"
                />
              </div>
              <div className="form-group">
                <label>Instagram Business Account ID</label>
                <input
                  type="text"
                  value={formData.meta.instagramAccountId}
                  onChange={(e) => handleInputChange('meta', 'instagramAccountId', e.target.value)}
                  placeholder="Your Instagram Business Account ID"
                />
              </div>
            </div>
            <div className="form-actions">
              <button
                onClick={() => handleGetOAuthUrl('meta')}
                className="btn btn-secondary"
              >
                <FaLink style={{ marginRight: '5px' }} />
                Get OAuth Authorization URL
              </button>
              <button
                onClick={() => handleSave('meta')}
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        )}

        {/* Google Configuration */}
        {activeTab === 'google' && (
          <div className="config-form">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>
                <FaGoogle style={{ marginRight: '10px', color: '#4285F4' }} />
                Google Ads API Configuration
              </h3>
              <button
                onClick={() => showHelp('google')}
                className="btn btn-link"
                style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#4285F4' }}
                title="Google Ads Setup Guide"
              >
                <FaBook />
                <span>How to Setup</span>
              </button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Client ID *</label>
                <input
                  type="text"
                  value={formData.google.clientId}
                  onChange={(e) => handleInputChange('google', 'clientId', e.target.value)}
                  placeholder="Your Google Client ID"
                />
              </div>
              <div className="form-group">
                <label>Client Secret *</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords['google_clientSecret'] ? 'text' : 'password'}
                    value={formData.google.clientSecret}
                    onChange={(e) => handleInputChange('google', 'clientSecret', e.target.value)}
                    placeholder="Your Google Client Secret"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => togglePasswordVisibility('google', 'clientSecret')}
                  >
                    {showPasswords['google_clientSecret'] ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Refresh Token</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords['google_refreshToken'] ? 'text' : 'password'}
                    value={formData.google.refreshToken}
                    onChange={(e) => handleInputChange('google', 'refreshToken', e.target.value)}
                    placeholder="Will be obtained via OAuth"
                    readOnly
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => togglePasswordVisibility('google', 'refreshToken')}
                  >
                    {showPasswords['google_refreshToken'] ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Customer ID</label>
                <input
                  type="text"
                  value={formData.google.customerId}
                  onChange={(e) => handleInputChange('google', 'customerId', e.target.value)}
                  placeholder="Your Google Ads Customer ID"
                />
              </div>
              <div className="form-group">
                <label>Developer Token</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords['google_developerToken'] ? 'text' : 'password'}
                    value={formData.google.developerToken}
                    onChange={(e) => handleInputChange('google', 'developerToken', e.target.value)}
                    placeholder="Your Google Ads Developer Token"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => togglePasswordVisibility('google', 'developerToken')}
                  >
                    {showPasswords['google_developerToken'] ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Redirect URI</label>
                <input
                  type="text"
                  value={formData.google.redirectUri}
                  onChange={(e) => handleInputChange('google', 'redirectUri', e.target.value)}
                  placeholder="http://localhost:5000/api/ai-config/google/oauth-callback"
                  readOnly
                />
                <small>Configure this in your Google Cloud Console</small>
              </div>
            </div>
            <div className="form-actions">
              <button
                onClick={() => handleGetOAuthUrl('google')}
                className="btn btn-secondary"
              >
                <FaLink style={{ marginRight: '5px' }} />
                Get OAuth Authorization URL
              </button>
              <button
                onClick={() => handleSave('google')}
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        )}

        {/* OpenRouter Configuration */}
        {activeTab === 'openrouter' && (
          <div className="config-form">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>
                <FaRobot style={{ marginRight: '10px', color: '#10B981' }} />
                OpenRouter (LLM) API Configuration
              </h3>
              <button
                onClick={() => showHelp('openrouter')}
                className="btn btn-secondary btn-sm"
                title="OpenRouter Setup Guide"
              >
                <FaBook />
                <span>How to Setup</span>
              </button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>
                  <FaKey style={{ marginRight: '5px' }} />
                  API Key *
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPasswords['openrouter_apiKey'] ? 'text' : 'password'}
                    value={formData.openrouter.apiKey}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      openrouter: { ...prev.openrouter, apiKey: e.target.value }
                    }))}
                    placeholder="Your OpenRouter API Key"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => togglePasswordVisibility('openrouter', 'apiKey')}
                    title={showPasswords['openrouter_apiKey'] ? 'Hide' : 'Show'}
                  >
                    {showPasswords['openrouter_apiKey'] ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                <small>Get your API key from <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer">OpenRouter Dashboard</a></small>
              </div>
              <div className="form-group">
                <label>
                  <FaLink style={{ marginRight: '5px' }} />
                  Base URL
                </label>
                <input
                  type="text"
                  value={formData.openrouter.baseUrl}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    openrouter: { ...prev.openrouter, baseUrl: e.target.value }
                  }))}
                  placeholder="https://openrouter.ai/api/v1"
                />
                <small>Default OpenRouter API endpoint</small>
              </div>
              <div className="form-group">
                <label>
                  <FaRobot style={{ marginRight: '5px' }} />
                  Default Model
                </label>
                <input
                  type="text"
                  value={formData.openrouter.defaultModel}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    openrouter: { ...prev.openrouter, defaultModel: e.target.value }
                  }))}
                  placeholder="openai/gpt-3.5-turbo"
                />
                <small>Default model to use (e.g., openai/gpt-3.5-turbo, openai/gpt-4, anthropic/claude-3-sonnet). This model will always be available in all workflows.</small>
              </div>
              <div className="form-group">
                <label>
                  <FaRobot style={{ marginRight: '5px' }} />
                  Additional Models (Optional)
                </label>
                <small style={{ display: 'block', marginBottom: '10px', color: '#666' }}>
                  Add additional models that will be available globally across all workflows. The default model above will always be available.
                </small>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                  {formData.openrouter.models && formData.openrouter.models.length > 0 && formData.openrouter.models.map((model, index) => (
                    <span
                      key={index}
                      style={{
                        padding: '6px 12px',
                        background: '#e7f3ff',
                        border: '1px solid #b3d9ff',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      {model}
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            openrouter: {
                              ...prev.openrouter,
                              models: prev.openrouter.models.filter((_, i) => i !== index)
                            }
                          }));
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc3545',
                          cursor: 'pointer',
                          fontSize: '14px',
                          padding: '0',
                          lineHeight: '1'
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value && !formData.openrouter.models?.includes(e.target.value)) {
                      setFormData(prev => ({
                        ...prev,
                        openrouter: {
                          ...prev.openrouter,
                          models: [...(prev.openrouter.models || []), e.target.value]
                        }
                      }));
                    }
                    e.target.value = '';
                  }}
                  style={{ width: '100%', padding: '8px', marginBottom: '8px' }}
                >
                  <option value="">Select a model to add...</option>
                  <optgroup label="OpenAI Models">
                    <option value="openai/gpt-4">GPT-4</option>
                    <option value="openai/gpt-4-turbo">GPT-4 Turbo</option>
                    <option value="openai/gpt-3.5-turbo">GPT-3.5 Turbo</option>
                    <option value="openai/gpt-4o">GPT-4o</option>
                    <option value="openai/gpt-4o-mini">GPT-4o Mini</option>
                  </optgroup>
                  <optgroup label="Anthropic Models">
                    <option value="anthropic/claude-3-opus">Claude 3 Opus</option>
                    <option value="anthropic/claude-3-sonnet">Claude 3 Sonnet</option>
                    <option value="anthropic/claude-3-haiku">Claude 3 Haiku</option>
                    <option value="anthropic/claude-3-5-sonnet">Claude 3.5 Sonnet</option>
                  </optgroup>
                  <optgroup label="Google Models">
                    <option value="google/gemini-pro">Gemini Pro</option>
                    <option value="google/gemini-pro-1.5">Gemini Pro 1.5</option>
                    <option value="google/gemini-flash-1.5">Gemini Flash 1.5</option>
                  </optgroup>
                  <optgroup label="Other Models">
                    <option value="meta-llama/llama-3-70b-instruct">Llama 3 70B</option>
                    <option value="mistralai/mistral-large">Mistral Large</option>
                    <option value="perplexity/llama-3-sonar-large-32k-online">Perplexity Sonar Large</option>
                  </optgroup>
                </select>
                <input
                  type="text"
                  placeholder="Or enter custom model (e.g., custom/model-name)"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      const customModel = e.target.value.trim();
                      if (!formData.openrouter.models?.includes(customModel)) {
                        setFormData(prev => ({
                          ...prev,
                          openrouter: {
                            ...prev.openrouter,
                            models: [...(prev.openrouter.models || []), customModel]
                          }
                        }));
                        e.target.value = '';
                      }
                    }
                  }}
                  style={{ width: '100%', padding: '8px' }}
                />
                <small>Add multiple models that will be available globally. These models can be selected in workflows in addition to the default model.</small>
              </div>
              <div className="form-group">
                <label>
                  <FaKey style={{ marginRight: '5px' }} />
                  Organization ID (Optional)
                </label>
                <input
                  type="text"
                  value={formData.openrouter.organizationId}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    openrouter: { ...prev.openrouter, organizationId: e.target.value }
                  }))}
                  placeholder="Your Organization ID"
                />
                <small>Optional: For organization-level usage tracking</small>
              </div>
            </div>
            <div className="form-actions">
              <button
                onClick={() => handleSave('openrouter')}
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save OpenRouter Configuration'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Help Modal */}
      <HelpModal
        show={helpModal.show}
        title={helpModal.title}
        content={helpModal.content}
        onClose={() => setHelpModal({ show: false, title: '', content: '' })}
      />
    </div>
  );
};

export default AIConfigManager;

